import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { AiChatSidebar } from "@/components/ai/ai-chat-sidebar";
import { DeleteProjectDialog } from "@/components/projects/delete-project-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  useDefaultTopUpOfferCode,
  useRuntimeCreditCost,
} from "@/features/pricing/useRuntimePricing";
import {
  projectBookAssemblyStore,
} from "@/stores/project-book-assembly-store";
import { useProjectAiStore } from "@/stores/projectAiStore";
import { useProjectShellStore } from "@/stores/projectShellStore";
import { useSubscriptionStore } from "@/stores/subscriptionStore";

function isProjectAiAuthoringRoute(pathname: string) {
  if (/^\/projects\/[^/]+\/stories\/book-assembly(?:\/|$)/.test(pathname)) {
    return false;
  }

  return /^\/projects\/[^/]+\/(?:stories|lore|entities)(?:\/|$)/.test(pathname);
}

export function ProjectShellLayout() {
  const { t } = useTranslation();
  const chatMessageCreditCost = useRuntimeCreditCost("ai_chat");
  const defaultTopUpOfferCode = useDefaultTopUpOfferCode();
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const clearNotice = useProjectShellStore((state) => state.clearNotice);
  const currentProjectSummary = useProjectShellStore((state) => state.currentProjectSummary);
  const dashboardStatus = useProjectShellStore((state) => state.dashboardStatus);
  const hydrateProjectDashboard = useProjectShellStore(
    (state) => state.hydrateProjectDashboard,
  );
  const notice = useProjectShellStore((state) => state.notice);
  const aiCreditsRemaining = useProjectAiStore((state) => state.creditsRemaining);
  const clearChat = useProjectAiStore((state) => state.clearChat);
  const hydrateProjectAi = useProjectAiStore((state) => state.hydrateProjectContext);
  const projectAiSidebar = useProjectAiStore((state) => state);
  const requestProjectAiChat = useProjectAiStore((state) => state.requestProjectAiChat);
  const resetProjectAi = useProjectAiStore((state) => state.resetProjectContext);
  const setLoreSummary = useProjectAiStore((state) => state.setLoreSummary);
  const toggleProjectAi = useProjectAiStore((state) => state.toggleOpen);
  const checkout = useSubscriptionStore((state) => state.checkout);
  const creditBalance = useSubscriptionStore((state) => state.creditBalance);
  const setCreditBalance = useSubscriptionStore((state) => state.setCreditBalance);
  const tier = useSubscriptionStore((state) => state.tier);
  const showProjectAiSidebar = isProjectAiAuthoringRoute(location.pathname);

  useEffect(() => {
    if (!projectId) {
      return;
    }

    void hydrateProjectDashboard(projectId);
  }, [hydrateProjectDashboard, projectId]);

  useEffect(() => {
    if (!projectId) {
      return;
    }

    void projectBookAssemblyStore.getState().loadLaunchState(projectId);
  }, [projectId]);

  useEffect(() => {
    if (!projectId || !currentProjectSummary) {
      return;
    }

    hydrateProjectAi(projectId, currentProjectSummary.title);
  }, [currentProjectSummary, hydrateProjectAi, projectId]);

  useEffect(() => {
    if (!projectId) {
      resetProjectAi();
    }
  }, [projectId, resetProjectAi]);

  useEffect(() => {
    if (!showProjectAiSidebar && projectAiSidebar.open) {
      toggleProjectAi(false);
    }
  }, [projectAiSidebar.open, showProjectAiSidebar, toggleProjectAi]);

  useEffect(() => {
    if (!currentProjectSummary) {
      return;
    }

    setLoreSummary(
      t("story.ai.chat.scopeSummary", {
        genre: currentProjectSummary.genre,
        title: currentProjectSummary.title,
      }),
    );
  }, [currentProjectSummary, setLoreSummary, t]);

  useEffect(() => {
    if (typeof aiCreditsRemaining === "number") {
      setCreditBalance(aiCreditsRemaining);
    }
  }, [aiCreditsRemaining, setCreditBalance]);

  if (dashboardStatus === "loading" && !currentProjectSummary) {
    return (
      <Card>
        <CardContent className="p-10">
          <p className="text-lg text-muted-foreground">{t("projects.dashboard.loading")}</p>
        </CardContent>
      </Card>
    );
  }

  if (!currentProjectSummary) {
    return (
      <Card>
        <CardContent className="space-y-6 p-10">
          <Button asChild variant="ghost">
            <Link to="/app">
              <ArrowLeft className="h-4 w-4" />
              {t("projects.actions.backToPortfolio")}
            </Link>
          </Button>
          <div className="space-y-3">
            <h1 className="text-4xl text-foreground">{t("projects.dashboard.missingTitle")}</h1>
            <p className="max-w-2xl text-base leading-7 text-muted-foreground">
              {t("projects.dashboard.missing")}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const chatStatusLabel = tier === "free"
    ? t("story.ai.chat.lockedStatus")
    : t("story.ai.chat.creditStatus", {
        cost: chatMessageCreditCost,
        remaining: aiCreditsRemaining ?? creditBalance,
      });

  const handleAiPromptAction = () => {
    const prompt = projectAiSidebar.prompt;

    if (!prompt) {
      return;
    }

    if (prompt.kind === "upgrade") {
      void checkout("crafter_monthly", location.pathname);
      return;
    }

    if (prompt.kind === "top-up") {
      void checkout(defaultTopUpOfferCode, location.pathname);
    }
  };

  return (
    <div className="project-shell-page">
      {notice ? (
        <div className="project-shell-notice" role={notice.kind === "error" ? "alert" : "status"}>
          <div className="flex items-start justify-between gap-3">
            <p>{t(notice.message)}</p>
            <button
              className="text-muted-foreground transition-colors hover:text-foreground"
              onClick={clearNotice}
              type="button"
            >
              {t("projects.actions.dismiss")}
            </button>
          </div>
        </div>
      ) : null}

      <Outlet />

      {showProjectAiSidebar ? (
        <AiChatSidebar
          onAskQuestion={(question) => {
            void requestProjectAiChat(question).catch(() => undefined);
          }}
          onClearChat={clearChat}
          onPromptAction={handleAiPromptAction}
          onToggleOpen={toggleProjectAi}
          sidebar={projectAiSidebar}
          statusLabel={chatStatusLabel}
        />
      ) : null}

      <DeleteProjectDialog
        activeProjectId={currentProjectSummary.projectId}
        onDeleted={() => {
          navigate("/app");
        }}
      />
    </div>
  );
}
